import Breadcrumbs from "@/components/Breadcrumbs";
import Button from "@/components/Button";
import ButtonGroup from "@/components/ButtonGroup";
import { DateInput, SelectInput } from "@/components/InputFields";
import Navigation from "@/components/Navigation";
import PageHeader from "@/components/PageHeader";
import { SecondaryButtonGroup } from "@/components/SecondaryButtonGroup";
import SkeletonTextLine from "@/components/SkeletonTextLine";
import Spinner from "@/components/Spinner";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/chadcn/Resizable";
import { Separator } from "@/components/chadcn/Separator";
import {
  useBloodGlucoseObservationsQuery,
  useDiabetesClinicalNotesQuery,
  useDiabetesPatientQuery,
  useOrganizationQuery,
  useUpdateDiabetesMedicationsMutation,
  useUpdatePersonalizedDiabetesProtocolMutation,
} from "@/data/queries";
import { BloodGlucoseObservation } from "@/model/BloodGlucoseObservation";
import { hasFeatureFlag } from "@/model/Organization";
import { DiabetesPatient } from "@/model/Patient";
import { AddClinicalNoteDialog } from "@/screens/patient-details/AddClinicalNoteDialog";
import ClinicalNotesList from "@/screens/patient-details/ClinicalNotesList";
import MedicationBox from "@/screens/patient-details/MedicationBox";
import OffboardPatientDialog from "@/screens/patient-details/OffboardPatientDialog";
import PatientInfoBox from "@/screens/patient-details/PatientInfoBox";
import SummarySinceLastReviewBanner from "@/screens/patient-details/SummarySinceLastReviewBanner";
import { UpdateDiabetesProtocolDialog } from "@/screens/patient-details/UpdateDiabetesProtocolDialog";
import { UpdateMedicationDialog } from "@/screens/patient-details/UpdateMedicationDialog";
import DiabetesProtocolBox from "@/screens/patient-details/diabetes/DiabetesProtocolBox";
import DiabetesTableLegend from "@/screens/patient-details/diabetes/DiabetesTableLegend";
import PatientNotesSection from "@/screens/patient-details/diabetes/PatientNotesSection";
import SidePanel from "@/screens/patient-details/diabetes/SidePanel";
import FoodLogTable from "@/screens/patient-details/diabetes/food-log/FoodLogTable";
import MeasurementLogTable from "@/screens/patient-details/diabetes/measurements/MeasurementTable";
import MedicationLegend from "@/screens/patient-details/diabetes/overview/MedicationLegend";
import OverviewTable from "@/screens/patient-details/diabetes/overview/OverviewTable";
import cn from "@/util/cn";
import { DISTANT_FUTURE, DISTANT_PAST, localDateRange } from "@/util/date-utils";
import { useSearchParams } from "@/util/hooks/useSearchParams";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { Instant, LocalDate, ZoneId } from "@js-joda/core";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";

type TableLength = "7-days" | "30-days";
type Table = "overview" | "food-log" | "measurement-log";

type QueryParams = {
  table: Table;
  start: string;
  length: TableLength;
};

const DiabetesPatientDetailsPage = () => {
  const { patientId: patientIdFromParams } = useParams();
  const navigate = useNavigate();
  const patientId = patientIdFromParams!;

  const [searchParams, setSearchParams] = useSearchParams<QueryParams>({
    table: "overview",
    start: LocalDate.now(ZoneId.SYSTEM).minusDays(6).toString(),
    length: "7-days",
  });

  const [isEditingProtocol, setIsEditingProtocol] = useState(false);
  const [isEditingMedication, setIsEditingMedication] = useState(false);
  const [lastSelectedObservation, setSelectedObservation] =
    useState<BloodGlucoseObservation | null>(null);
  const [isClinicalNoteDialogOpen, setIsClinicalNoteDialogOpen] = useState(false);
  const [isOffboardDialogOpen, setIsOffboardDialogOpen] = useState(false);

  const setSelectedTable = (table: Table) => setSearchParams((prev) => ({ ...prev, table }));
  const setStartDate = (startDate: LocalDate) =>
    setSearchParams((prev) => ({ ...prev, start: startDate.toString() }));
  const setTableLength = (tableLength: TableLength) =>
    setSearchParams((prev) => ({ ...prev, length: tableLength }), { replace: true });

  const { table: selectedTable, length: tableLength, start: startDateString } = searchParams;
  const tableLengthInDays = tableLength === "7-days" ? 7 : 30;
  const startDate = LocalDate.parse(startDateString);
  const endDateExclusive = startDate.plusDays(tableLengthInDays);
  const tableStart = startDate.atStartOfDay(ZoneId.SYSTEM).toInstant();
  const tableEndExclusive = endDateExclusive.atStartOfDay(ZoneId.SYSTEM).toInstant();

  const isInTableRange = ({ dateTime }: BloodGlucoseObservation) => {
    const instant = dateTime.toInstant();
    return !instant.isBefore(tableStart) && instant.isBefore(tableEndExclusive);
  };
  const selectedObservation =
    lastSelectedObservation && isInTableRange(lastSelectedObservation)
      ? lastSelectedObservation
      : null;

  const {
    isLoading: isLoadingPatient,
    isError: isErrorPatient,
    data: patient = null,
  } = useDiabetesPatientQuery(patientId);

  const {
    isPending: isLoadingObservations,
    isError: isErrorObservations,
    data: { observations = [], statistics = {} } = {},
  } = useBloodGlucoseObservationsQuery({
    patientId: patientId,
    from: tableStart,
    to: tableEndExclusive,
  });

  const {
    isPending: isLoadingClinicalNotes,
    isError: isErrorClinicalNotes,
    data: clinicalNotes = [],
  } = useDiabetesClinicalNotesQuery(patientId);

  const {
    data: organization,
    // TODO: isError: isErrorOrganization,
  } = useOrganizationQuery();

  const {
    mutateAsync: updatePersonalizedProtocol,
    isPending: isUpdatingPersonalizedProtocol,
    isError: isErrorUpdatingPersonalizedProtocol,
  } = useUpdatePersonalizedDiabetesProtocolMutation({ patientId });

  const {
    mutateAsync: updateMedications,
    isPending: isUpdatingMedications,
    isError: isErrorUpdatingMedications,
  } = useUpdateDiabetesMedicationsMutation({ patientId });

  const showMedication = hasFeatureFlag(organization, "MEDICATION_MANAGEMENT_DIABETES");

  const onPreviousDateRangeSelected = () => {
    setStartDate(startDate.minusDays(tableLengthInDays));
  };

  const onNextDateRangeSelected = () => {
    setStartDate(startDate.plusDays(tableLengthInDays));
  };

  const onObservationSelected = (observation: BloodGlucoseObservation) => {
    setSelectedObservation(observation);
    document
      .querySelector(`[data-id="${observation.id}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const navigateToHypertension = () => navigate(`/patients/${patientId}/hypertension`);

  // -1 ms to make sure first reading is included
  const lastReviewTimeForTable = isLoadingPatient
    ? DISTANT_FUTURE
    : (patient?.lastReviewTime ?? patient?.firstReadingTime?.minusMillis(1) ?? DISTANT_PAST);

  const dates = localDateRange({
    startInclusive: startDate,
    endExclusive: endDateExclusive,
  });

  const isInTable = (time: Instant) =>
    time.equals(tableStart) || (time.isAfter(tableStart) && time.isBefore(tableEndExclusive));

  const patientName =
    !isLoadingPatient && patient ? `${patient.firstName} ${patient.lastName}` : "...";

  // Only if the current time is inside the time range defined by the table,
  // and the last review time is on or after the table's start, are we sure
  // that all the unreviewed data (new readings and cells without a reading
  // that have not yet been reviewed) is within the current view. If the
  // current time is not in the currently visible table's time range, there
  // almost definitially could be new data as the time has progressed since
  // the last review. If the last review time is before the table start,
  // there is unreviewed data in a previous time period.
  const currentTableContainsAllNewReadings =
    isInTable(Instant.now()) && lastReviewTimeForTable.compareTo(tableStart) >= 0;

  const breadcrumbs = [{ title: "Patient overview", href: "/" }, { title: patientName }];

  const navigateToPatientOverview = () => {
    navigate("/", { replace: true });
  };

  return (
    <>
      <div className="flex h-full flex-col">
        <Navigation className="px-8 py-4 print:hidden" />
        <Breadcrumbs breadcrumbs={breadcrumbs} className="mb-5 mt-4 shrink-0 px-8 print:hidden" />
        <ResizablePanelGroup direction="horizontal" className="grow px-8 print:overflow-visible!">
          <ResizablePanel defaultSize={75} minSize={40} className="h-full">
            <div className="h-full overflow-y-auto pb-8 pe-4 print:pb-8">
              <div className="mb-5 flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  {isLoadingPatient ? (
                    <SkeletonTextLine className="my-0.5 h-9 w-64" />
                  ) : (
                    <PageHeader title={patientName} />
                  )}
                  {!isLoadingPatient && patient?.hasHypertension && (
                    <ButtonGroup
                      aria-label="Protocol type"
                      values={[
                        { value: "diabetes", title: "Diabetes" },
                        { value: "hypertension", title: "Hypertension" },
                      ]}
                      value={"diabetes"}
                      onChange={navigateToHypertension}
                      className="print:hidden"
                    />
                  )}
                </div>
                {!isLoadingPatient && (
                  <Menu as="div" className="relative print:hidden">
                    <MenuButton as={Button} variant="neutral" className="material-icons p-2">
                      more_vert
                    </MenuButton>
                    <MenuItems
                      transition
                      anchor="bottom end"
                      className="absolute right-0 z-10 mt-2 w-48 rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 transition duration-200 ease-out focus:outline-hidden data-closed:scale-95 data-closed:opacity-0 data-enter:duration-200 data-leave:duration-150"
                    >
                      <MenuItem>
                        <button
                          onClick={() => setIsOffboardDialogOpen(true)}
                          className="block w-full cursor-pointer px-4 py-2 text-left text-gray-700 hover:bg-gray-100"
                        >
                          Offboard patient
                        </button>
                      </MenuItem>
                    </MenuItems>
                  </Menu>
                )}
              </div>
              <SummarySinceLastReviewBanner
                patientStatus={patient?.status}
                lastReviewTime={patient?.lastReviewTime}
                unreviewedReadingsSummaryData={patient ? summaryDataFor(patient) : []}
                isLoading={isLoadingPatient || !patient?.unreviewedReadingsUpToDate}
                className="mb-4 print:hidden"
              />
              <div className="mb-8 flex gap-4">
                <PatientInfoBox className="shrink" patient={patient} isLoading={isLoadingPatient} />
                <DiabetesProtocolBox
                  personalizedClinicalProtocol={patient?.personalizedClinicalProtocol}
                  isLoading={isLoadingPatient}
                  onEditSelected={() => setIsEditingProtocol(true)}
                />
                {showMedication && (
                  <MedicationBox
                    className="min-w-72 max-w-md"
                    medications={patient?.medications ?? []}
                    isLoading={isLoadingPatient || isUpdatingMedications}
                    onEditSelected={() => setIsEditingMedication(true)}
                  />
                )}
              </div>
              <div className="flex items-center gap-2 pb-4 print:hidden">
                <Button
                  variant="neutral"
                  className="material-icons p-2"
                  onClick={onPreviousDateRangeSelected}
                >
                  chevron_left
                </Button>
                <DateInput
                  className="max-w-36 grow"
                  max={LocalDate.now(ZoneId.SYSTEM).plusDays(1).toString()}
                  value={startDate.toString()}
                  onChange={(e) => setStartDate(LocalDate.parse(e.target.value))}
                />
                <SelectInput
                  className="max-w-24 grow"
                  value={tableLength}
                  options={[
                    { value: "7-days", title: "7 days" },
                    { value: "30-days", title: "30 days" },
                  ]}
                  onChange={setTableLength}
                />
                <Button
                  variant="neutral"
                  className="material-icons p-2"
                  onClick={onNextDateRangeSelected}
                >
                  chevron_right
                </Button>
                {isLoadingObservations && <Spinner className="ms-4" />}
                <SecondaryButtonGroup
                  className="ml-auto shadow-xs"
                  values={[
                    { value: "overview" as Table, title: "Overview" },
                    { value: "food-log" as Table, title: "Food log" },
                    {
                      value: "measurement-log" as Table,
                      title: "Measurements",
                    },
                  ]}
                  selectedValue={selectedTable}
                  onButtonPressed={setSelectedTable}
                />
              </div>
              {isErrorPatient && (
                <p className="py-2 text-red-400">
                  Failed to retrieve patient details. Please try again later.
                </p>
              )}
              {isErrorObservations && (
                <p className="py-2 text-red-400">Failed to load chart. Please try again later.</p>
              )}
              <div className="flex flex-col gap-8">
                <h2 className="mb-4 hidden text-2xl text-primary/90 print:block print:break-before-page">
                  Overview
                </h2>
                <OverviewTable
                  className={cn("print:block", selectedTable === "overview" ? "block" : "hidden")}
                  dates={dates}
                  observations={observations}
                  statistics={statistics}
                  lastReviewTime={lastReviewTimeForTable}
                  selectedObservation={selectedObservation}
                  onObservationSelected={onObservationSelected}
                  showMedication={showMedication}
                />
                <h2 className="mb-4 hidden text-2xl text-primary/90 print:block print:break-before-page">
                  Food log
                </h2>
                <FoodLogTable
                  className={cn("print:block", selectedTable === "food-log" ? "block" : "hidden")}
                  dates={dates}
                  observations={observations}
                  lastReviewTime={lastReviewTimeForTable}
                  selectedObservation={selectedObservation}
                  onObservationSelected={onObservationSelected}
                />
                <h2 className="mb-4 hidden text-2xl text-primary/90 print:block print:break-before-page">
                  Measurements
                </h2>
                <MeasurementLogTable
                  className={cn(
                    "print:block",
                    selectedTable === "measurement-log" ? "block" : "hidden"
                  )}
                  dates={dates}
                  observations={observations}
                  lastReviewTime={lastReviewTimeForTable}
                  selectedObservation={selectedObservation}
                  onObservationSelected={onObservationSelected}
                />
              </div>
              <div className="mt-3 flex items-start justify-between gap-4">
                <DiabetesTableLegend />
                {selectedTable === "overview" && showMedication && <MedicationLegend />}
              </div>
              <PatientNotesSection
                observations={observations}
                className="mt-8 hidden print:block print:break-before-page"
              />
              <Separator orientation="horizontal" className="mt-8 print:hidden" />
              <div className="mb-4 mt-8 flex items-center justify-between print:break-before-page">
                <h2 className="text-2xl text-primary/90">Clinical notes</h2>
                <Button
                  variant="primary"
                  disabled={isLoadingClinicalNotes}
                  className="ml-auto print:hidden"
                  onClick={() => setIsClinicalNoteDialogOpen(true)}
                >
                  <span className="material-icons me-1">add</span>Add note
                </Button>
              </div>
              <ClinicalNotesList
                clinicalNotes={clinicalNotes}
                filterOptionsForPrinting={{
                  timeRangeStart: tableStart,
                  timeRangeEnd: tableEndExclusive,
                }}
              />
              {isLoadingClinicalNotes && <Spinner />}
              {isErrorClinicalNotes ? (
                <p className="py-2 text-red-400">
                  Failed to load clinical notes. Please try again later.
                </p>
              ) : !isLoadingClinicalNotes && clinicalNotes.length === 0 ? (
                <div className="italic text-primary/60">No notes available</div>
              ) : null}
            </div>
          </ResizablePanel>
          <ResizableHandle className="mb-4 w-[2px] print:hidden" />
          <ResizablePanel defaultSize={25} minSize={15} className="h-full ps-4 print:hidden">
            <div className="h-full grow overflow-y-auto pb-8">
              <SidePanel
                className="min-h-full rounded-md border border-gray-200 bg-gray-50"
                observations={observations}
                selectedObservation={selectedObservation ?? undefined}
              />
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
      {organization && patient && (
        <AddClinicalNoteDialog
          open={isClinicalNoteDialogOpen}
          onOpenChange={setIsClinicalNoteDialogOpen}
          patientId={patientId}
          protocolType="diabetes"
          prescribingCaregiver={patient.prescribingCaregiver}
          currentTableContainsAllNewReadings={currentTableContainsAllNewReadings}
          organization={organization}
        />
      )}
      {patient && (
        <OffboardPatientDialog
          open={isOffboardDialogOpen}
          onOpenChange={setIsOffboardDialogOpen}
          patient={patient}
          prescriptionId={patient?.prescriptionId || ""}
          deliveryDate={patient?.deliveryDate}
          onConfirm={navigateToPatientOverview}
        />
      )}
      {patient && organization && (
        <UpdateDiabetesProtocolDialog
          open={isEditingProtocol}
          onOpenChange={setIsEditingProtocol}
          isLoading={isUpdatingPersonalizedProtocol}
          isError={isErrorUpdatingPersonalizedProtocol}
          diagnosis={patient.diagnosis}
          personalizedClinicalProtocol={patient.personalizedClinicalProtocol}
          organizationClinicalProtocols={organization.diabetesClinicalProtocols}
          onSubmit={async (personalizedClinicalProtocol) => {
            try {
              await updatePersonalizedProtocol({
                prescriptionId: patient.prescriptionId,
                personalizedClinicalProtocol: personalizedClinicalProtocol,
              });
              setIsEditingProtocol(false);
            } catch (error) {
              console.error(error);
            }
          }}
        />
      )}
      <UpdateMedicationDialog
        open={isEditingMedication}
        onOpenChange={setIsEditingMedication}
        medications={patient?.medications ?? []}
        isError={isErrorUpdatingMedications}
        onSubmit={async (medications) => {
          setIsEditingMedication(false);
          try {
            await updateMedications(medications);
          } catch (error) {
            console.error(error);
          }
        }}
      />
    </>
  );
};

const summaryDataFor = (patient: DiabetesPatient) => {
  const { emergencyLow, criticalLow, criticalHigh, total } = patient.unreviewedReadings;
  const other = total - emergencyLow - criticalLow - criticalHigh;

  const emergencyLowSummaryData = {
    amount: emergencyLow,
    classificationText: "emergency low",
    className: "text-measurement-text-emergency",
  };

  const criticalLowSummaryData = {
    amount: criticalLow,
    classificationText: "critical low",
    className: "text-measurement-text-warning",
  };

  const criticalHighSummaryData = {
    amount: criticalHigh,
    classificationText: "critical high",
    className: "text-measurement-text-emergency",
  };

  const otherSummaryData = {
    amount: other,
    classificationText: other === total ? undefined : "other",
    className: "text-primary/90",
  };

  return [
    emergencyLowSummaryData,
    criticalLowSummaryData,
    criticalHighSummaryData,
    otherSummaryData,
  ];
};

export default DiabetesPatientDetailsPage;
